import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartPlus, faBolt, faCheck } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import CartContext from '../../../middlewares/cart/CartContext'
import HomeNav from './HomeNav'

// ── Types ─────────────────────────────────────────────────────────────────────
interface TimeUnit {
  name: string
}

interface Plan {
  name: string
  description: string
  list_description: string[]
  price: number
  price_mxn: number
  quantity: number
  expiry_time: number
  best_value: boolean
  Time: TimeUnit
  product_name?: string
}

interface ProductData {
  name: string
  description: string
  image: string
  Plans: Plan[]
}

interface CartCtx {
  cart: Plan[]
  addItem: (item: Plan) => void
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND        = '#6eb446'
const BRAND_DARK   = '#5a9438'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.35)'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SkeletonHero: React.FC = () => (
  <>
    <style>{`
      @keyframes shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }
      .shimmer {
        background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
        background-size: 600px 100%;
        animation: shimmer 1.4s ease-in-out infinite;
      }
    `}</style>
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '5rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div className="shimmer" style={{ width: 240, height: 40, borderRadius: 8 }} />
      <div className="shimmer" style={{ width: '80%', height: 18, borderRadius: 6 }} />
      <div className="shimmer" style={{ width: '60%', height: 18, borderRadius: 6 }} />
      <div className="shimmer" style={{ width: 280, height: 280, borderRadius: 16, marginTop: 12 }} />
    </div>
  </>
)

// ── Plan card ─────────────────────────────────────────────────────────────────
interface PlanCardProps {
  plan: Plan
  isMexico: boolean
  onAddToCart: (plan: Plan) => void
  onBuyNow: (plan: Plan) => void
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, isMexico, onAddToCart, onBuyNow }) => {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)

  const price = isMexico
    ? `$${parseFloat(String(plan.price_mxn)).toFixed(2)} MXN`
    : `$${parseFloat(String(plan.price)).toFixed(2)} USD`

  const licenseLabel = plan.quantity > 1 ? t('pages.product.licenses') : t('pages.product.license')
  const timeLabel    = plan.expiry_time > 1
    ? t(`utilities.${plan.Time.name}s`)
    : t(`utilities.${plan.Time.name}`)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        backgroundColor: '#fff',
        borderRadius: 20,
        border: `2px solid ${plan.best_value ? BRAND : hovered ? '#cbd5e1' : '#e2e8f0'}`,
        boxShadow: plan.best_value
          ? BRAND_SHADOW
          : hovered ? '0 12px 40px rgba(0,0,0,0.1)' : '0 2px 12px rgba(0,0,0,0.05)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'box-shadow 0.25s, transform 0.25s, border-color 0.25s',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Best value badge */}
      {plan.best_value && (
        <div style={{
          backgroundColor: BRAND,
          color: '#fff',
          textAlign: 'center',
          padding: '6px 0',
          fontWeight: 700,
          fontSize: '0.78rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>
          ⭐ {t('pages.product.bestvalue')}
        </div>
      )}

      <div style={{ padding: '1.75rem 1.5rem', display: 'flex', flexDirection: 'column', flex: 1, gap: 16 }}>

        {/* Plan name */}
        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
          {plan.name}
        </h3>

        {/* Price */}
        <div>
          <p style={{ margin: '0 0 2px', fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('pages.product.price')}
          </p>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 800, color: plan.best_value ? BRAND : '#1e293b', lineHeight: 1 }}>
            {price}
          </p>
        </div>

        {/* License info */}
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5 }}>
          <strong style={{ color: '#374151' }}>{plan.quantity}</strong> {licenseLabel}{' '}
          {t('pages.product.for')}{' '}
          <strong style={{ color: '#374151' }}>{plan.expiry_time} {timeLabel}</strong>
        </p>

        {/* Description */}
        {plan.description && (
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6 }}>
            {plan.description}
          </p>
        )}

        {/* Feature list */}
        {plan.list_description.length > 0 && (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plan.list_description.map((item, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.83rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: BRAND, marginTop: 3, flexShrink: 0, fontSize: '0.75rem' }} />
                {item.toString()}
              </li>
            ))}
          </ul>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* CTAs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={() => onBuyNow(plan)}
            style={{
              width: '100%',
              padding: '11px',
              borderRadius: 9999,
              border: 'none',
              backgroundColor: BRAND,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              boxShadow: BRAND_SHADOW,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'filter 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)' }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)' }}
          >
            <FontAwesomeIcon icon={faBolt} />
            {t('pages.product.buynow')}
          </button>

          <button
            onClick={() => onAddToCart(plan)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: 9999,
              border: `1.5px solid ${BRAND}`,
              backgroundColor: 'transparent',
              color: BRAND,
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'background-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4fbf0' }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
          >
            <FontAwesomeIcon icon={faCartPlus} />
            {t('pages.product.addtocart')}
          </button>
        </div>

      </div>
    </div>
  )
}

// ── Product ───────────────────────────────────────────────────────────────────
const Product: React.FC = () => {
  const { addItem, cart }              = useContext(CartContext) as CartCtx
  const { product: product_name }      = useParams<{ product: string }>()
  const navigate                       = useNavigate()

  const [product, setProduct]         = useState<ProductData | null>(null)
  const [isInMexico, setIsInMexico]   = useState(false)
  const [loading, setLoading]         = useState(true)

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true)
      await Promise.all([fetchProduct(), fetchLocation()])
      setLoading(false)
    }
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product_name])

  const fetchProduct = async () => {
    try {
      const res = await axios.get<{ message: ProductData }>(`${url}products/show/${product_name}`)
      setProduct(res.data.message)
    } catch (_) {}
  }

  const fetchLocation = async () => {
    try {
      const res = await axios.get<{ country: string; countryIso: string }>(`${url}api/location`)
      const { country, countryIso } = res.data
      setIsInMexico(country.toLowerCase() === 'mexico' || countryIso.toLowerCase() === 'mx')
    } catch (_) {
      setIsInMexico(false)
    }
  }

  const handleAddToCart = (plan: Plan) => {
    const enriched = { ...plan, product_name: product?.name ?? '' }
    const price = isInMexico
      ? `$${parseFloat(String(plan.price_mxn)).toFixed(2)} MXN`
      : `$${parseFloat(String(plan.price)).toFixed(2)} USD`

    Swal.fire({
      icon: 'success',
      html: `
        <div style="display:flex;align-items:center;gap:12px;text-align:left">
          <img src="${url}images/${product?.image}" width="48" style="border-radius:8px;object-fit:contain" alt="${plan.name}"/>
          <div>
            <p style="margin:0 0 4px;font-weight:700;font-size:.9rem">${plan.name}</p>
            <p style="margin:0;font-size:.82rem;color:#64748b">${price}</p>
          </div>
        </div>`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      showCloseButton: true,
      timer: 3000,
      showClass: { popup: 'animate__animated animate__fadeIn' },
      hideClass: { popup: 'animate__animated animate__fadeOut' },
      didOpen: toast => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      },
    })
    addItem(enriched)
  }

  const handleBuyNow = (plan: Plan) => {
    const enriched = { ...plan, product_name: product?.name ?? '' }
    if (cart.length === 0) addItem(enriched)
    navigate('/checkout')
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <HomeNav />
      <SkeletonHero />
    </>
  )

  if (!product) return (
    <>
      <HomeNav />
      <div style={{ textAlign: 'center', padding: '5rem 1.5rem', color: '#64748b' }}>
        <p style={{ fontSize: '1.2rem' }}>Product not found.</p>
      </div>
    </>
  )

  return (
    <>
      <HomeNav />
      <div className="animate__animated animate__fadeIn">

      {/* Hero */}
      <section style={{ borderBottom: '4px solid #000', padding: '4rem 1.5rem 3rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: '#f4fbf0',
            border: `2px solid #c8edb0`,
            marginBottom: '1.5rem',
          }}>
            <img
              src={`${url}images/${product.image}`}
              alt={product.name}
              width={72}
              height={72}
              style={{ objectFit: 'contain' }}
            />
          </div>

          <h1 style={{
            fontSize: 'clamp(2rem, 6vw, 3.5rem)',
            fontWeight: 900,
            color: BRAND,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            marginBottom: '1rem',
          }}>
            {product.name}
          </h1>

          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.15rem)',
            color: '#475569',
            lineHeight: 1.75,
            maxWidth: 580,
            margin: '0 auto',
          }}>
            {product.description}
          </p>
        </div>
      </section>

      {/* Plans */}
      <section style={{ backgroundColor: '#f8faf6', padding: '4rem 1.5rem' }}>
        {product.Plans.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem 0' }}>
            <p style={{ fontSize: '1.1rem' }}>No plans available for this product yet.</p>
          </div>
        ) : (
          <div style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1.5rem',
            alignItems: 'start',
          }}>
            {product.Plans.map((plan, i) => (
              <PlanCard
                key={i}
                plan={plan}
                isMexico={isInMexico}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
              />
            ))}
          </div>
        )}
      </section>

    </div>
    </>
  )
}

export default Product
